import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import type { SlideContentBlock, SlideLeafBlock } from '@hcc/types';
import { BLOCK_GROUPS, BLOCK_META } from './blockMeta';
import { makeBlock, moveBlock } from './blockOps';
import { LeafBlockEditor } from './LeafBlockEditor';
import { ColumnsBlockEditor } from './ColumnsBlockEditor';
import { InsertMenu, type InsertGroup } from '../InsertMenu';
import { SortableList, SortableRow, DragHandle, reorder } from '../SortableList';

/**
 * The ordered block list for a slide (or one column cell): a card per
 * block with reorder/delete, the per-type editor inline, and the add
 * palette underneath — the same interaction as the page list, one level
 * down. Column cells pass allowColumns=false (one nesting level only).
 */
export function BlockEditorList({
  blocks,
  onChange,
  allowColumns,
}: {
  blocks: SlideContentBlock[];
  onChange: (next: SlideContentBlock[]) => void;
  allowColumns: boolean;
}) {
  function update(index: number, next: SlideContentBlock) {
    onChange(blocks.map((b, i) => (i === index ? next : b)));
  }
  const groups: InsertGroup[] = BLOCK_GROUPS.map((g) => ({
    title: g.title,
    items: g.types
      .filter((t) => allowColumns || t !== 'columns')
      .map((t) => ({
        key: t,
        label: BLOCK_META[t].label,
        description: BLOCK_META[t].description,
        icon: BLOCK_META[t].icon,
        onSelect: () => onChange([...blocks, makeBlock(t)]),
      })),
  })).filter((g) => g.items.length > 0);

  return (
    <div className="space-y-2">
      <SortableList
        ids={blocks.map((b) => b.id)}
        onReorder={(from, to) => onChange(reorder(blocks, from, to))}
        className="space-y-2"
      >
        {blocks.map((block, i) => {
          const Icon = BLOCK_META[block.type].icon;
          return (
            <SortableRow
              key={block.id}
              id={block.id}
              className={(dragging) =>
                `rounded-token-sm border bg-surface p-2.5 ${
                  dragging ? 'border-accent shadow-token' : 'border-line/10'
                }`
              }
            >
              {({ dragHandle }) => (
                <>
                  <div className="mb-2 flex items-center gap-1.5">
                    <DragHandle
                      dragHandle={dragHandle}
                      label={`Reorder ${BLOCK_META[block.type].label} block`}
                    />
                    <Icon className="h-3.5 w-3.5 shrink-0 text-fg-muted" />
                    <span className="flex-1 text-xs font-medium uppercase tracking-wide text-fg-dim">
                      {BLOCK_META[block.type].label}
                    </span>
                    <button
                      type="button"
                      aria-label="Move block up"
                      disabled={i === 0}
                      onClick={() => onChange(moveBlock(blocks, i, -1))}
                      className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Move block down"
                      disabled={i === blocks.length - 1}
                      onClick={() => onChange(moveBlock(blocks, i, 1))}
                      className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2 disabled:opacity-30"
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete block"
                      onClick={() => onChange(blocks.filter((_, j) => j !== i))}
                      className="rounded-token-sm p-1 text-fg-muted hover:bg-surface-2"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {block.type === 'columns' ? (
                    <ColumnsBlockEditor block={block} onChange={(next) => update(i, next)} />
                  ) : (
                    <LeafBlockEditor
                      block={block}
                      onChange={(next) => update(i, next as SlideContentBlock)}
                    />
                  )}
                </>
              )}
            </SortableRow>
          );
        })}
      </SortableList>
      <InsertMenu triggerLabel="Add block" groups={groups} />
    </div>
  );
}

/** A column cell is a leaf-only block list. */
export function LeafBlockList({
  blocks,
  onChange,
}: {
  blocks: SlideLeafBlock[];
  onChange: (next: SlideLeafBlock[]) => void;
}) {
  return (
    <BlockEditorList
      blocks={blocks}
      onChange={(next) => onChange(next as SlideLeafBlock[])}
      allowColumns={false}
    />
  );
}
